import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { SVLoader, SVButton, SVForm, SVAutocomplete } from '../../core';
import {
  useDeactivateCompany,
  useGetCompanyListForSelection,
} from '@/hooks/requests/useCompanies';
import { useForm } from 'react-hook-form';
import { useGetTeammateListForSelection } from '@/hooks/requests/useTeammates';
import { StatusQueryParam } from '@/types/common';
import { TeammateRole } from '@/types/teammates';

type FormData = {
  companyId: string;
  teammateId: string;
};

type CompanyDeactivateModalProps = {
  id: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CompanyDeactivateModal({
  id,
  open,
  setOpen,
}: CompanyDeactivateModalProps) {
  const {
    canDeactivateCompany,
    isCanDeactivateCompanyPending,
    deactivateCompany,
    isDeactivateCompanyPending,
  } = useDeactivateCompany(id, open);

  const formMethods = useForm<FormData>({
    defaultValues: {
      companyId: '',
      teammateId: '',
    },
    mode: 'onBlur',
  });
  const companyId = formMethods.watch('companyId');
  const { companiesList } = useGetCompanyListForSelection(
    { status: StatusQueryParam.ACTIVE },
    open,
  );
  const { teammatesList, isTeammatesListPending } =
    useGetTeammateListForSelection(
      {
        company_id: companyId,
        status: StatusQueryParam.ACTIVE,
        roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR],
      },
      !!companyId,
    );

  if (isCanDeactivateCompanyPending) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  async function handleTeammateSwapDeactivate(data: FormData) {
    try {
      await deactivateCompany(data.teammateId);
      showToast({
        type: 'success',
        data: { content: 'Company has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  const showNoTeammateWarning =
    teammatesList.length === 0 && companyId && !isTeammatesListPending;

  if (!canDeactivateCompany) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <SVForm methods={formMethods} onSubmit={handleTeammateSwapDeactivate}>
          <DialogContent>
            <Typography variant="h4">Cannot Deactive Company</Typography>
            <Typography mb={2}>
              This company has active customers, businesses, bank accounts,
              and/or bank applications. Please select an advisor to associate
              this company's customers, businesses, bank accounts and bank
              applications to.
            </Typography>
            <SVAutocomplete
              label="Company"
              name="companyId"
              options={companiesList.filter(c => c.value !== id)}
              rules={{ required: true }}
            />
            {showNoTeammateWarning ? (
              <Typography color="error">
                The selected company has no teammates. Please try another
                company
              </Typography>
            ) : (
              <SVAutocomplete
                label="Advisor"
                name="teammateId"
                options={teammatesList}
                rules={{ required: true }}
                disabled={!companyId || isTeammatesListPending}
              />
            )}
          </DialogContent>
          <DialogActions>
            <SVButton variant="outlined" onClick={() => setOpen(false)}>
              Back
            </SVButton>
            <SVButton
              type="submit"
              onClick={formMethods.handleSubmit(handleTeammateSwapDeactivate)}>
              Confirm
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>
    );
  }

  async function handleDeactivate() {
    try {
      await deactivateCompany('');
      showToast({
        type: 'success',
        data: { content: 'Company has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h4">Deactivate Company?</Typography>
        <Typography>
          Are you sure you would like to deactivate this company?
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="outlined" onClick={() => setOpen(false)}>
          No, Don't Deactivate
        </SVButton>
        <SVButton
          variant="error"
          isLoading={isDeactivateCompanyPending}
          onClick={handleDeactivate}>
          Yes, Deactivate
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
